import { useState } from 'react';
import getWorkflow from '@jira/project-settings__get-workflow/src/index.tsx';
import { getReadViewWorkflow } from '@jira/project-settings__get-read-view-workflow/src/index.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { WorkflowIdentifier, WorkflowState } from './types.tsx';
import { messages } from './messages.tsx';

const ERROR_LOGGER_PATTERN = '#workflow-view-diagram-modal#';

export const useWorkflowData = (
	cloudId: string,
	workflowIdentifier: WorkflowIdentifier,
	projectId?: string,
) => {
	const [state, setState] = useState<WorkflowState>({
		data: undefined,
		isLoading: true,
		hasError: false,
		customErrorMessage: '',
	});
	const isAdmin = useIsAdmin();
	const { formatMessage } = useIntl();

	useEffectWithoutConcurrency(() => {
		const fetchWorkflow = async () => {
			setState((prev) => ({ ...prev, isLoading: true, hasError: false }));

			try {
				if (isAdmin) {
					const workflowResponse = await getWorkflow(cloudId, workflowIdentifier);
					setState({
						data: workflowResponse,
						isLoading: false,
						hasError: false,
					});
					return;
				}

				if (!projectId) {
					fireErrorAnalytics({
						meta: {
							id: `${ERROR_LOGGER_PATTERN}.missing.project.id`,
							packageName: 'jiraWorkflowViewDiagramModal',
							teamName: 'helix',
						},

						error: new Error('Missing projectId for non-admin user'),
					});
					setState((prev) => ({
						...prev,
						hasError: true,
						isLoading: false,
						customErrorMessage: formatMessage(messages.noPermissionToViewThisWorkflow),
					}));
					return;
				}

				const workflowResponse = await getReadViewWorkflow({
					cloudId,
					projectId,
					...workflowIdentifier,
				});
				setState({
					data: workflowResponse,
					isLoading: false,
					hasError: false,
				});
			} catch (error: unknown) {
				if (error instanceof FetchError && error.statusCode) {
					log.safeErrorWithoutCustomerData(
						`${ERROR_LOGGER_PATTERN}.fetch.failure.${error.statusCode}`,
						error.message || 'Fetch failure with empty error message',
						error,
					);
				} else if (error instanceof TypeError) {
					log.safeErrorWithoutCustomerData(
						`${ERROR_LOGGER_PATTERN}.fetch.failure`,
						error.message,
						error,
					);
				}
				fireErrorAnalytics({
					meta: {
						id: `${ERROR_LOGGER_PATTERN}.fetch.error`,
						packageName: 'jiraWorkflowViewDiagramModal',
						teamName: 'helix',
					},
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: error as Error,
				});
				setState((prev) => ({ ...prev, hasError: true, isLoading: false }));
			}
		};

		fetchWorkflow();
	}, [cloudId, workflowIdentifier]);

	return state;
};
